import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql } from '@apollo/client'
import { saveReferralConfiguration } from '../../../apollo'
import { Box, Typography, Input, Button, Alert, Switch } from '@mui/material'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'

const SAVE_REFERRAL_CONFIGURATION = gql`
  ${saveReferralConfiguration}
`

// The referrer earns these credits once the person they invited completes
// their first order.
function ReferralConfiguration(props) {
  const { t } = props
  const [referralEnabled, setReferralEnabled] = useState(true)
  const [referralCredits, setReferralCredits] = useState('')
  // Shared with the loyalty programme: credits and wallet money are one pot
  const [creditsPerPound, setCreditsPerPound] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [mutate, { loading }] = useMutation(SAVE_REFERRAL_CONFIGURATION)

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  useEffect(() => {
    setReferralEnabled(props.referralEnabled !== false)
    setReferralCredits(
      props.referralCredits === null ||
        typeof props.referralCredits === 'undefined'
        ? ''
        : String(props.referralCredits)
    )
    setCreditsPerPound(
      props.creditsPerPound === null ||
        typeof props.creditsPerPound === 'undefined'
        ? ''
        : String(props.creditsPerPound)
    )
  }, [props.referralEnabled, props.referralCredits, props.creditsPerPound])

  useEffect(() => {
    if (!successMessage) return
    const timeoutId = setTimeout(() => setSuccessMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [successMessage])

  useEffect(() => {
    if (!errorMessage) return
    const timeoutId = setTimeout(() => setErrorMessage(''), 3000)
    return () => clearTimeout(timeoutId)
  }, [errorMessage])

  // What one referral is actually worth in money, at the current rate
  const rate = Number(creditsPerPound) || 0
  const referralValue = rate > 0 ? Number(referralCredits || 0) / rate : null

  const onSave = e => {
    e.preventDefault()
    if (!Number(creditsPerPound)) {
      setErrorMessage(t('CreditsPerPoundRequired'))
      return
    }
    mutate({
      variables: {
        referralEnabled,
        referralCredits: Number(referralCredits || 0),
        creditsPerPound: Number(creditsPerPound)
      },
      onCompleted: () => setSuccessMessage(t('Configuration saved')),
      onError: error => {
        setErrorMessage(t('An error occurred while saving configuration.'))
        console.error('Mutation error:', error)
      }
    })
  }

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            {t('ReferralProgramme')}
          </Typography>
        </Box>
        <Box ml={10} mt={1}>
          <label>{referralEnabled ? t('Disable') : t('Enable')}</label>
          <Switch
            checked={referralEnabled}
            onChange={e => setReferralEnabled(e.target.checked)}
            style={{ color: 'black' }}
          />
        </Box>
      </Box>

      <Box className={classes.form}>
        <form>
          <Box>
            <Typography className={classes.labelText}>
              {t('ReferralCredits')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-referral-credits"
              name="input-referral-credits"
              placeholder={t('ReferralCredits')}
              type="number"
              min={0}
              value={referralCredits}
              onChange={e => {
                const value = e.target.value
                setReferralCredits(
                  value === '' ? '' : String(Math.max(0, Number(value)))
                )
              }}
              disableUnderline
              className={globalClasses.input}
            />
            <Typography className={classes.labelText}>
              {t('ReferralCreditsHelp')}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.labelText}>
              {t('CreditsPerPound')}
            </Typography>
            <Input
              style={{ marginTop: -1 }}
              id="input-referral-credits-per-pound"
              name="input-referral-credits-per-pound"
              placeholder={t('CreditsPerPound')}
              type="number"
              min={1}
              value={creditsPerPound}
              onChange={e => {
                const value = e.target.value
                setCreditsPerPound(
                  value === '' ? '' : String(Math.max(0, Number(value)))
                )
              }}
              disableUnderline
              className={globalClasses.input}
            />
            <Typography className={classes.labelText}>
              {t('CreditsPerPoundSharedHelp')}
            </Typography>
          </Box>
          {referralValue !== null ? (
            <Typography className={classes.labelText}>
              {t('ReferralWorth', {
                credits: Number(referralCredits || 0),
                currencySymbol: props.currencySymbol || '',
                amount: referralValue.toFixed(2)
              })}
            </Typography>
          ) : null}
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={onSave}>
              {t('Save')}
            </Button>
          </Box>
          <Box mt={2}>
            {successMessage && (
              <Alert
                className={globalClasses.alertSuccess}
                variant="filled"
                severity="success">
                {successMessage}
              </Alert>
            )}
            {errorMessage && (
              <Alert
                className={globalClasses.alertError}
                variant="filled"
                severity="error">
                {errorMessage}
              </Alert>
            )}
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(ReferralConfiguration)
